import { cx } from "@jugl-web/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { ResizableProps, ResizeCallbackData } from "react-resizable";

interface UseResizableColumnOptions {
  width: number;
  minWidth?: number;
  maxWidth?: number;
  onResizeCommit?: (width: number) => void;
}

const DEFAULT_MIN_WIDTH = 100;
const DEFAULT_MAX_WIDTH = 1000;

export const useResizableColumn = ({
  width,
  minWidth,
  maxWidth,
  onResizeCommit,
}: UseResizableColumnOptions) => {
  const [isResizing, setIsResizing] = useState(false);

  const [localWidth, setLocalWidth] = useState(width);

  const isResizingRef = useRef(false);

  const handleResize = useCallback((_: unknown, data: ResizeCallbackData) => {
    setLocalWidth(data.size.width);
  }, []);

  const handleResizeStart = useCallback(() => {
    document.body.style.cursor = "col-resize";
    isResizingRef.current = true;
    setIsResizing(true);
  }, []);

  const handleResizeStop = useCallback(
    (_: unknown, data: ResizeCallbackData) => {
      document.body.style.cursor = "default";
      isResizingRef.current = false;
      setIsResizing(false);
      onResizeCommit?.(data.size.width);
    },
    [onResizeCommit]
  );

  useEffect(() => {
    if (!isResizingRef.current) {
      setLocalWidth(width);
    }
  }, [width]);

  const resizableProps: ResizableProps = {
    width: localWidth,
    height: 0,
    onResize: handleResize,
    onResizeStart: handleResizeStart,
    onResizeStop: handleResizeStop,
    resizeHandles: ["e"],
    minConstraints:
      typeof minWidth === "number" ? [minWidth, 0] : [DEFAULT_MIN_WIDTH, 0],
    maxConstraints:
      typeof maxWidth === "number"
        ? [maxWidth, Infinity]
        : [DEFAULT_MAX_WIDTH, Infinity],
  };

  const handleClassName = cx(
    "absolute top-0 right-0 h-full w-2 cursor-col-resize bg-blue-200 opacity-0 hover:opacity-60 transition-opacity duration-200 z-10",
    isResizing && "opacity-60"
  );

  const resizeOffset = isResizing ? localWidth - width : 0;

  return {
    isResizing,
    resizableProps,
    handleClassName,
    currentWidth: localWidth,
    resizeOffset,
  };
};
